import { jumboMediaAPI } from "./service";
import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { toast } from "react-toastify";
import { TOAST_DURATION } from "../utils/helpers/constants";
import prepareHeaders from "./helpers/prepareHeaders";
import {
  authRefreshMutex,
  handleRefreshFailure,
  isAuthRefreshPaused,
  refreshAccessToken,
} from "../utils/helpers/auth/authSession";
import { getLoggedUserToken } from "../utils/helpers/tokenStorage";

export const BASE_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:49314/"
    : process.env.REACT_APP_MEDIA_API_URL;

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders,
});

export const baseQueryWithReAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const isChangingAccount = (api.getState() as { auth: { isChangingAccount: boolean } })
    .auth.isChangingAccount;

  // During an account switch, `changeAccount` (useAccount.ts) flips the active token in storage
  // (markUserAsLoggedById) and calls resetApiState() in the same synchronous tick. resetApiState
  // refetches the header's still-mounted `getUserById` subscription using the OLD user's id (Redux
  // hasn't re-rendered yet), but prepareHeaders now attaches the NEW user's token. The backend
  // `UsersController.UserGet` guard (`User.Identity.GetUserId() != id`) then 401s on that mismatch.
  // A component-level `skip` can't prevent this — React hasn't re-rendered when resetApiState fires.
  // The base query is the only layer that sees isChangingAccount at request time, so gate the
  // identity-scoped `getUserById` here. Scoped to that one endpoint on purpose: other requests made
  // mid-switch (e.g. translation loading via setReactConsts) must still go through. After the switch
  // settles, LangSelect re-renders with the new id + new token and fetches cleanly.
  if (isChangingAccount && api.endpoint === "getUserById") {
    return {
      error: {
        status: "CUSTOM_ERROR",
        error: "Account switch in progress",
      },
    };
  }

  await authRefreshMutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  if ("error" in result) {
    const status = (result.error as any).originalStatus || result.error.status;

    switch (status) {
      case 401: {
        // A guest (no token) has no session to refresh or expire — return the 401
        // to the caller, mirroring authenticatedFetch's `!accessToken` guard. Without
        // this, an authed 401 on a public/shared page (e.g. the broadcaster-overlay
        // call on /projects/sharedvideoadproject/:token) falls through to
        // handleRefreshFailure() and redirects guests to /account/login.
        if (isChangingAccount || isAuthRefreshPaused() || !getLoggedUserToken()) {
          break;
        }

        if (!authRefreshMutex.isLocked()) {
          const newToken = await refreshAccessToken();

          if (newToken) {
            result = await baseQuery(args, api, extraOptions);
          } else {
            toast.error("Your session has expired. Please log in again.", {
              autoClose: TOAST_DURATION,
            });
            api.dispatch(jumboMediaAPI.util.resetApiState());
            handleRefreshFailure();
          }
        } else {
          await authRefreshMutex.waitForUnlock();
          result = await baseQuery(args, api, extraOptions);
        }
        break;
      }

      case 409: {
        // spec-38 — a 409 from getReDeliveryOrder is not a business conflict: it is how the legacy
        // WebAPIErrorExceptionFilterAttribute reports "no such order / not your client's order"
        // (ReDeliveryManager.GetOwnedOrder throws LocalizableInvalidOperationException for both). The
        // re-delivery pages turn that into a redirect, so a generic "Conflict" toast alongside it is
        // noise — and on the confirmation page, which polls every 4s, it was one toast every 4
        // seconds. The API now answers 404 for that case, so this only bites while a newer SPA runs
        // against an older API build. Scoped to that one endpoint deliberately: every other 409 in the
        // app is still a real conflict worth showing.
        if (
          !window.location.pathname.includes("campaigns") &&
          api.endpoint !== "createBrandShort" &&
          api.endpoint !== "getReDeliveryOrder"
        ) {
          toast("Conflict", {
            type: "error",
            autoClose: TOAST_DURATION,
          });
        }
        break;
      }
    }
  }

  return result;
};
